import React from "react";
import Card from "components/card";
import Button from "components/button/Button";
import DropzoneFile from "components/dropzone";
import TextField from "components/fields/TextField";
import InputField from "components/fields/InputField";
import { AxiosResponse } from "axios";
import { toast } from "react-hot-toast";
import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { addBrand, getBrand, updateBrand } from "api/BrandAPI";
import { useNavigate, useParams } from "react-router-dom";

import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";

const BrandForm: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [files, setFiles] = React.useState<File[]>([]);
  const [defaultImg, setDefaultImg] = React.useState<string>("");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({});

  const dropFile = (files: File[]) => {
    setFiles([...files]);
  };

  const removeFile = (files: File[]) => {
    setFiles([...files]);
  };

  const arrangeFiles = (files: File[]) => {
    setFiles([...files]);
  };

  const changeFile = (files: File[]) => {
    setFiles([...files]);
  };

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    setIsLoading(true);

    if (!defaultImg && !files[0]) {
      toast.error("Foto / Logo brand harus diisi!");
      setIsLoading(false);
      return;
    }

    const payload = {
      ...data,
      image: files[0],
      name: data?.name,
      description: data?.description,
    };

    let promise = null;
    if (!slug) {
      promise = addBrand(payload);
    } else {
      promise = updateBrand(slug, payload);
    }

    toast.promise(promise, {
      loading: "Menyimpan data brand...",
      success: (res: AxiosResponse) => {
        navigate("/admin/brands");
        setIsLoading(false);
        return "Berhasil menyimpan data brand!";
      },
      error: (err: any) => {
        setIsLoading(false);
        return err?.response?.data?.message || "Gagal menyimpan data brand!";
      },
    });
  };

  const getDetailData = (slug: string): void => {
    const promise = getBrand(slug);

    toast.promise(promise, {
      loading: "Mengambil data brand...",
      success: (res) => {
        if (res.status === 200) {
          delete res.data.data.created_at;
          delete res.data.data.updated_at;
          delete res.data.data.id;
          delete res.data.data.slug;

          reset({
            ...res.data.data,
          });

          if (res.data.data.image) setDefaultImg(res.data.data.image);

          return res.data.message || "Berhasil mengambil data brand!";
        }
      },
      error: (err) => {
        console.error(err);
        return err?.response?.data?.message || "Gagal mengambil data brand!";
      },
    });
  };

  React.useEffect(() => {
    if (slug) {
      getDetailData(slug);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card extra={"w-full h-full sm:overflow-auto px-6 pb-3 mt-3"}>
      <header className="relative flex items-center justify-between pt-4">
        <div className="text-xl font-bold text-navy-700 dark:text-white">
          Tambah Brand
        </div>
      </header>

      <form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
        <div className="mt-8">
          <div className="flex flex-col">
            <label htmlFor="images" className="label required">
              Foto / Logo Brand
            </label>
            <DropzoneFile
              text="Foto / Logo Brand"
              accept={{
                "image/*": [".jpg", ".png", ".jpeg", ".webp"],
              }}
              maxSize={2048000}
              maxFiles={1}
              files={files}
              dropFile={(files: File[]) => dropFile(files)}
              removeFile={(files: File[]) => removeFile(files)}
              arrangeFiles={(files: File[]) => arrangeFiles(files)}
              changeFile={(files: File[]) => changeFile(files)}
            />
          </div>
          {defaultImg && (
            <div className="my-3 flex flex-col">
              <label htmlFor="images" className="label">
                Foto / Logo Lama Brand
              </label>
              <div className="mx-auto flex h-48 w-auto flex-col items-center justify-center rounded-md border border-gray-300">
                <img
                  src={`${process.env.REACT_APP_PUBLIC_URL}/` + defaultImg}
                  alt="coupon"
                  className="h-full w-full rounded-md object-cover"
                />
              </div>
            </div>
          )}
          <p className="mb-3 ml-3 mt-7 font-medium text-gray-600">
            Detail Brand
          </p>
          <InputField
            extra="mb-3"
            label="Nama Brand"
            placeholder="Masukkan nama brand"
            id="name"
            type="text"
            variant="outline"
            register={register}
            errors={errors}
            validationSchema={{
              required: "Nama Brand harus diisi!",
            }}
          />
          <TextField
            extra="mb-3 flex-1"
            label="Deskripsi"
            placeholder="Deskripsi brand"
            id="description"
            variant="outline"
            register={register}
            errors={errors}
            validationSchema={{}}
          />
        </div>
        <Button
          children="Simpan"
          size="fit"
          variant="primary"
          extra="mt-7 mb-5"
          isLoading={isLoading}
        />
      </form>
    </Card>
  );
};

export default BrandForm;
